// C-4 — Rango horario de la agenda, preferencia POR USUARIO. Hook compartido por
// MonitorAgendaPage y SesionesPage: lee la preferencia 'agenda_hours' al montar
// (default 8–24 si no existe) y la guarda con reflejo inmediato (optimista) en la
// rejilla. Envuelve el servicio genérico userPreferences (misma clave que usará
// la app en C-5). No duplica lógica de lectura/escritura.

import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { getUserPreference, setUserPreference } from './userPreferences';

export const AGENDA_HOURS_KEY = 'agenda_hours';
export const AGENDA_HOURS_DEFAULT = { start: 8, end: 24 } as const;

export interface AgendaHours {
  start: number;
  end: number;
}

// Rango válido = horas enteras con 0 <= start < end <= 24 (espeja la validación
// de la BD, mig 178). Se usa para descartar valores corruptos al leer y al guardar.
export function isValidAgendaHours(h: unknown): h is AgendaHours {
  if (typeof h !== 'object' || h === null) return false;
  const { start, end } = h as Record<string, unknown>;
  return (
    typeof start === 'number' &&
    typeof end === 'number' &&
    Number.isInteger(start) &&
    Number.isInteger(end) &&
    start >= 0 &&
    end <= 24 &&
    start < end
  );
}

export function useAgendaHours() {
  const { profile } = useAuthStore();
  const [hours, setHours] = useState<AgendaHours>({ ...AGENDA_HOURS_DEFAULT });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      const pref = await getUserPreference<AgendaHours>(AGENDA_HOURS_KEY);
      if (!active) return;
      if (isValidAgendaHours(pref)) setHours(pref);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  // Guarda el nuevo rango. Optimista: se refleja en la rejilla al instante y se
  // revierte si la escritura falla. Rechaza rangos inválidos sin tocar la BD.
  const save = useCallback(
    async (next: AgendaHours) => {
      if (!profile) return;
      if (!isValidAgendaHours(next)) {
        setError('Rango no válido (0 ≤ inicio < fin ≤ 24).');
        return;
      }
      const prev = hours;
      setHours(next);
      setSaving(true);
      setError('');
      const { error: e } = await setUserPreference(profile.id, AGENDA_HOURS_KEY, next);
      if (e) {
        setHours(prev);
        setError('No se pudo guardar el horario.');
      }
      setSaving(false);
    },
    [hours, profile]
  );

  return { hourStart: hours.start, hourEnd: hours.end, hours, save, loading, saving, error };
}
